
import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

type Testimonial = Tables<"testimonials">;

// Align with the database's service_category enum values
const testimonialSchema = z.object({
  author_name: z.string().min(2, "Name must be at least 2 characters").optional(),
  content: z.string().min(10, "Testimonial content must be at least 10 characters"),
  company_name: z.string().optional(),
  role_title: z.string().default("Professional"),
  industry: z.string().default("Technology"),
  service_category: z.enum(["Consulting", "Coaching", "Training"]).default("Consulting"),
  rating: z.number().min(1).max(5).optional(),
  approved: z.boolean().default(false),
});

// Ensure content is required in our form values type
type TestimonialFormValues = z.infer<typeof testimonialSchema> & {
  content: string; // Explicitly mark content as required
};

interface TestimonialFormProps {
  testimonial?: Testimonial | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const TestimonialForm = ({ testimonial, onSuccess, onCancel }: TestimonialFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const form = useForm<TestimonialFormValues>({
    resolver: zodResolver(testimonialSchema),
    defaultValues: {
      author_name: "",
      content: "", // This is required, but we start with empty string
      company_name: "",
      role_title: "Professional",
      industry: "Technology",
      service_category: "Consulting",
      rating: 5,
      approved: false,
    },
  });

  useEffect(() => {
    if (testimonial) {
      form.reset({
        author_name: testimonial.author_name || "",
        content: testimonial.content, // This comes from DB and is non-optional
        company_name: testimonial.company_name || "",
        role_title: testimonial.role_title || "Professional",
        industry: testimonial.industry || "Technology",
        service_category: testimonial.service_category || "Consulting",
        rating: testimonial.rating || 5,
        approved: testimonial.approved || false,
      });
    }
  }, [testimonial, form]);

  const { mutate: saveTestimonial, isPending } = useMutation({
    mutationFn: async (values: TestimonialFormValues) => {
      if (testimonial?.id) {
        // Update existing testimonial
        const { data, error } = await supabase
          .from("testimonials")
          .update(values as Required<Pick<TestimonialFormValues, 'content'>> & Omit<TestimonialFormValues, 'content'>)
          .eq("id", testimonial.id)
          .select()
          .single();

        if (error) throw new Error(error.message);
        return data;
      } else {
        // Create new testimonial
        const { data, error } = await supabase
          .from("testimonials")
          .insert([values as Required<Pick<TestimonialFormValues, 'content'>> & Omit<TestimonialFormValues, 'content'>])
          .select()
          .single();

        if (error) throw new Error(error.message);
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
      toast({
        title: testimonial ? "Testimonial updated" : "Testimonial created",
        description: testimonial
          ? "The testimonial has been updated successfully."
          : "The testimonial has been created successfully.",
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to save testimonial: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  function onSubmit(values: TestimonialFormValues) {
    // The Zod schema already ensures content is not empty, but we'll do a runtime check for type safety
    if (!values.content.trim()) {
      form.setError("content", { 
        type: "manual", 
        message: "Testimonial content is required" 
      });
      return;
    }
    
    // At this point, we know content is not empty
    saveTestimonial(values);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="author_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Author Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} value={field.value || ""} />
                </FormControl>
                <FormDescription>Name of the person giving the testimonial</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="company_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input placeholder="Acme Inc." {...field} value={field.value || ""} />
                </FormControl>
                <FormDescription>Company the person represents</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="role_title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role/Title</FormLabel>
                <FormControl>
                  <Input placeholder="CEO" {...field} value={field.value || "Professional"} />
                </FormControl>
                <FormDescription>Person's role or job title</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="industry"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Industry</FormLabel>
                <FormControl>
                  <Input placeholder="Technology" {...field} value={field.value || "Technology"} />
                </FormControl>
                <FormDescription>Industry the person works in</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="service_category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Category</FormLabel>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  <option value="Consulting">Consulting</option>
                  <option value="Coaching">Coaching</option>
                  <option value="Training">Training</option>
                </select>
                <FormDescription>Category of service received</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rating"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rating (1-5)</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    min={1} 
                    max={5} 
                    {...field} 
                    value={field.value || 5}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormDescription>Rating from 1 to 5 stars</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Testimonial Content</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="The service was excellent..."
                  className="min-h-[150px]"
                  {...field}
                />
              </FormControl>
              <FormDescription>The full testimonial text</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="approved"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Approve Testimonial</FormLabel>
                <FormDescription>
                  Approved testimonials will be displayed publicly on the website
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
       
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? "Saving..." : testimonial ? "Update Testimonial" : "Create Testimonial"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default TestimonialForm;
